"use client";

import Link from "next/link";
import { useState } from "react";
import CloudButton from "./CloudButton";
import type { NavItem } from "@/lib/nav";

type CloudNavItemProps = {
  item: NavItem;
  color: string;
  className?: string;
};

/**
 * A cloud nav button that may carry a dropdown of subcategory links.
 *
 * Open/closed is React state rather than `group-hover` + `group-focus-within`.
 * With the CSS version the panel would not close when a link was clicked: the
 * click focuses the anchor, so `:focus-within` held the panel open over the
 * page it had just opened, and only a click elsewhere dismissed it. Driving it
 * from state means a click can close it outright, and because the pointer
 * fires no new mouseenter while it sits still, it stays closed until the user
 * actually moves away and back.
 */
export default function CloudNavItem({ item, color, className = "" }: CloudNavItemProps) {
  const [open, setOpen] = useState(false);

  function closeAndBlur(event: React.MouseEvent<HTMLAnchorElement>) {
    setOpen(false);
    // Without this the anchor keeps focus after the click, and the next
    // keyboard interaction would re-open the panel on top of the new page.
    event.currentTarget.blur();
  }

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        // Ignore focus moving between the cloud and its own panel links.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <CloudButton href={item.href} label={item.label} color={color} className="w-full" />

      {item.children && open && (
        <div className="absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-2">
          <ul className="rounded-2xl border-2 border-black bg-white p-2 shadow-xl">
            {item.children.map((child) => (
              <li key={child.href}>
                <Link
                  href={child.href}
                  onClick={closeAndBlur}
                  className="block rounded-xl px-4 py-3 text-base font-extrabold text-[#35263a] transition-colors hover:bg-[#fff7ef] focus-visible:bg-[#fff7ef] focus-visible:outline-none"
                >
                  {child.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
